import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useCompanies } from './hooks';
import { useCurrentDomain } from "./context/DomainContext";
import { getAllIntakeSheets, assignIntakeSheets, deleteIntakeSheetMapping } from './services/masterDataService';
import { selectStyles as sharedSelectStyles } from './helpers';
import { THEME } from './theme/themeColors';

const selectStyles = {
    ...sharedSelectStyles,
    clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
};

const cardHeaderStyle = {
    color: THEME.onPrimary, fontWeight: 600, fontSize: 14, padding: '8px', backgroundColor: THEME.primary,
};

const Field = ({ label, required, htmlFor }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <label htmlFor={htmlFor} style={{ fontSize: '10px', fontWeight: 600, color: 'black', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 0 }}>
            {label}
            {required && <span style={{ color: '#dc3545', marginLeft: '3px', fontSize: '14px', lineHeight: '1' }}>*</span>}
        </label>
    </div>
);

const Spinner = () => (
    <div className="text-center py-4">
        <div className="spinner-border spinner-border-sm" style={{ color: THEME.primary }} role="status">
            <span className="visually-hidden">Loading...</span>
        </div>
    </div>
);

const ConfigureIntakeSheet = () => {
    const { domain } = useCurrentDomain();
    const [intakeSheets, setIntakeSheets] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedSheetIds, setSelectedSheetIds] = useState([]);
    const [mappedSheets, setMappedSheets] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [sheetSearch, setSheetSearch] = useState('');
    const [addLoading, setAddLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [sheetsLoading, setSheetsLoading] = useState(false);
    const fetchSeqRef = useRef(0);

    const { companies, loading: companiesLoading } = useCompanies(domain);

    useEffect(() => {
        setSelectedCompany(null);
        setSheetSearch('');
    }, [domain]);

    useEffect(() => {
        if (!selectedCompany) {
            fetchSeqRef.current++;
            setIntakeSheets([]);
            setMappedSheets([]);
            setSelectedSheetIds([]);
            setSelectedRows([]);
            setSheetsLoading(false);
            return;
        }
        fetchSheets();
    }, [selectedCompany]);

    const fetchSheets = async () => {
        const seq = ++fetchSeqRef.current;
        const companyId = selectedCompany.value;
        setSheetsLoading(true);
        try {
            const data = await getAllIntakeSheets(companyId, domain);
            if (seq !== fetchSeqRef.current) return;
            setIntakeSheets(data.filter(s => !s.isAssigned));
            setMappedSheets(data.filter(s => s.isAssigned));
            setSelectedSheetIds([]);
            setSelectedRows([]);
        } catch (err) {
            if (seq !== fetchSeqRef.current) return;
            toast.error('Failed to load intake sheets.');
        } finally {
            if (seq === fetchSeqRef.current) setSheetsLoading(false);
        }
    };

    const handleAddMapping = async () => {
        if (!selectedCompany || selectedSheetIds.length === 0) return;
        setAddLoading(true);
        const loadingToast = toast.loading('Assigning intake sheets...');
        try {
            await assignIntakeSheets(selectedCompany.value, selectedSheetIds, domain);
            toast.dismiss(loadingToast);
            toast.success(`${selectedSheetIds.length} sheet(s) assigned successfully.`);
            await fetchSheets();
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || 'Failed to assign intake sheets.');
        } finally {
            setAddLoading(false);
        }
    };

    const handleDeleteMapping = async () => {
        if (!selectedCompany || selectedRows.length === 0) return;
        const toastId = toast(
            <div style={{ textAlign: 'center', minWidth: '250px' }}>
                <div style={{ fontWeight: '600', marginBottom: '10px' }}>
                    Delete {selectedRows.length} mapping(s)?
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '10px' }}>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => toast.dismiss(toastId)}>
                        Cancel
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => { toast.dismiss(toastId); performDelete(); }}>
                        Yes, Delete
                    </button>
                </div>
            </div>,
            { autoClose: false, closeButton: false, closeOnClick: false, draggable: false, className: 'bg-white shadow' }
        );
    };

    const performDelete = async () => {
        setDeleteLoading(true);
        const loadingToast = toast.loading('Deleting mappings...');
        try {
            await deleteIntakeSheetMapping(selectedCompany.value, selectedRows, domain);
            toast.dismiss(loadingToast);
            toast.success(`${selectedRows.length} mapping(s) deleted successfully.`);
            await fetchSheets();
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || 'Failed to delete mappings.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCompanyChange = (selected) => {
        setSelectedCompany(selected);
        setSelectedSheetIds([]);
        setSelectedRows([]);
        setSheetSearch('');
    };

    const filteredSheets = intakeSheets.filter(s =>
        (s.szFormName || '').toLowerCase().includes(sheetSearch.toLowerCase())
    );

    const allMappedSelected = mappedSheets.length > 0 && mappedSheets.every(s => selectedRows.includes(s.id));

    const handleSelectAll = () => {
        if (allMappedSelected) setSelectedRows([]);
        else setSelectedRows(mappedSheets.map(s => s.id));
    };

    return (
        <div className="container-fluid p-0 px-1">
            <div className="card-body">
                <div className="row g-2">
                    <div className="col-12 col-lg-6">
                        <div className="card border h-100" style={{ borderRadius: '6px' }}>
                            <div className="card-header" style={cardHeaderStyle}>
                                Add Intake Sheets
                            </div>
                            <div className="card-body p-2">
                                <div className="mb-2">
                                    <Field label="Company" required htmlFor="intake-company" />
                                    <Select
                                        inputId="intake-company"
                                        options={companies}
                                        value={selectedCompany}
                                        onChange={handleCompanyChange}
                                        isLoading={companiesLoading}
                                        placeholder="Select company..."
                                        isSearchable isClearable
                                        styles={selectStyles}
                                    />
                                </div>
                                <div className="mb-2">
                                    <Field label="Search Sheets" htmlFor="intake-sheet-search" />
                                    <input
                                        id="intake-sheet-search"
                                        type="text"
                                        className="form-control form-control-sm cis-input"
                                        placeholder="Filter sheet list..."
                                        value={sheetSearch}
                                        onChange={e => setSheetSearch(e.target.value)}
                                        disabled={!selectedCompany}
                                        style={{ fontSize: '12px' }}
                                    />
                                </div>
                                <div style={{ height: '280px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', background: '#fafafa' }}>
                                    {sheetsLoading ? (
                                        <Spinner />
                                    ) : filteredSheets.length === 0 ? (
                                        <div className="text-center py-4" style={{ color: '#adb5bd', fontSize: '12px' }}>
                                            {selectedCompany ? 'No unassigned sheets.' : 'Select a company first.'}
                                        </div>
                                    ) : (
                                        filteredSheets.map(s => {
                                            const checked = selectedSheetIds.includes(s.id);
                                            return (
                                                <label
                                                    key={s.id}
                                                    className="cis-row"
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px',
                                                        borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '12px',
                                                        background: checked ? THEME.primaryLight : undefined,
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => setSelectedSheetIds(prev =>
                                                            prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id]
                                                        )}
                                                        style={{ accentColor: THEME.primary }}
                                                    />
                                                    {s.szFormName}
                                                </label>
                                            );
                                        })
                                    )}
                                </div>
                                <div className="d-flex justify-content-center gap-2 mt-2">
                                    <button
                                        onClick={() => { setSelectedSheetIds([]); setSheetSearch(''); }}
                                        className="btn btn-outline-secondary btn-sm px-3"
                                        style={{ fontSize: '12px' }}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        onClick={handleAddMapping}
                                        disabled={addLoading || sheetsLoading || !selectedCompany || selectedSheetIds.length === 0}
                                        className="btn btn-sm btn-theme px-3 fw-semibold"
                                        style={{ fontSize: '12px' }}
                                    >
                                        {addLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                Saving...
                                            </>
                                        ) : (
                                            `Add Mapping (${selectedSheetIds.length})`
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-lg-6">
                        <div className="card border h-100" style={{ borderRadius: '6px' }}>
                            <div className="card-header" style={cardHeaderStyle}>
                                Mapped Intake Sheets
                            </div>
                            <div className="card-body p-2">
                                {mappedSheets.length > 0 && (
                                    <div className="d-flex align-items-center justify-content-between mb-1 px-1 py-1"
                                        style={{ borderBottom: '1px solid #e9ecef', minHeight: '28px' }}>

                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="selectAllMapped"
                                                checked={allMappedSelected}
                                                onChange={handleSelectAll}
                                                style={{ accentColor: THEME.primary, width: '14px', height: '14px', cursor: 'pointer' }}
                                            />
                                            <label className="form-check-label mb-0" style={{ fontSize: '11px', fontWeight: 600, color: '#495057', cursor: 'pointer', userSelect: 'none' }} htmlFor="selectAllMapped">
                                                Select All
                                            </label>
                                            <span style={{ fontSize: '10px', color: '#adb5bd' }}>
                                                ({mappedSheets.length} total)
                                            </span>
                                        </div>

                                        {selectedRows.length > 0 && (
                                            <span style={{
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                background: THEME.primaryLight,
                                                color: THEME.primaryHover,
                                                padding: '2px 8px',
                                                borderRadius: '20px',
                                                border: `1px solid ${THEME.alpha(0.25)}`
                                            }}>
                                                ✓ {selectedRows.length} selected
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div style={{ height: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                                    {sheetsLoading ? (
                                        <Spinner />
                                    ) : mappedSheets.length === 0 ? (
                                        <div className="text-center py-4" style={{ color: '#adb5bd', fontSize: '12px' }}>
                                            {selectedCompany ? 'No active mappings.' : 'Select a company first.'}
                                        </div>
                                    ) : (
                                        <table className="table table-sm table-hover mb-0" style={{ fontSize: '12px' }}>
                                            <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                                                <tr>
                                                    <th style={{ width: '36px', padding: '6px 10px' }}><span className="visually-hidden">Select</span></th>
                                                    <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}>Sheet Name</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {mappedSheets.map(item => {
                                                    const rowSelected = selectedRows.includes(item.id);
                                                    const cellBg = rowSelected ? THEME.primaryLight : undefined;
                                                    return (
                                                        <tr key={item.id}>
                                                            <td style={{ padding: '6px 10px', background: cellBg }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={rowSelected}
                                                                    onChange={() => setSelectedRows(prev =>
                                                                        prev.includes(item.id) ? prev.filter(id => id !== item.id) : [...prev, item.id]
                                                                    )}
                                                                    aria-label={`Select ${item.szFormName}`}
                                                                    style={{ accentColor: THEME.primary }}
                                                                />
                                                            </td>
                                                            <td style={{ padding: '6px 10px', background: cellBg }}>{item.szFormName}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <div className="d-flex justify-content-center mt-2">
                                    <button
                                        onClick={handleDeleteMapping}
                                        disabled={deleteLoading || sheetsLoading || selectedRows.length === 0}
                                        className="btn btn-sm btn-danger px-3"
                                        style={{ fontSize: '12px' }}
                                    >
                                        {deleteLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                Deleting...
                                            </>
                                        ) : (
                                            `Delete Selected (${selectedRows.length})`
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .cis-row:hover { background: ${THEME.alpha(0.06)}; }
                .cis-input:focus { border-color: ${THEME.primary}; box-shadow: 0 0 0 0.2rem ${THEME.alpha(0.2)}; }
            `}</style>
        </div>
    );
};

export default ConfigureIntakeSheet;